import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, interval, Observable, of, Subscription, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ProfileService } from 'src/app/services/profile.service';
import { kycRenewalText } from 'src/app/shared/kyc-renewal.texts';
import { getPendingPscSession, MangopayIdvPscSession, Profile } from 'src/app/shared/models/register.models';
import { Capacitor } from '@capacitor/core';
import { isMobileWeb, mangopayIframeCamera, requestMobileWebCamera } from 'src/app/shared/utils/camera-permission';

@Component({
  selector: 'fun-register-identity-verification',
  templateUrl: './register-identity-verification.component.html',
  styleUrls: ['./register-identity-verification.component.scss']
})
export class RegisterIdentityVerificationComponent implements OnInit, OnDestroy {

  @Input() profile: Profile;
  // Desactívalo cuando quien usa el componente ya muestra su propia pantalla final justo
  // después, para no encadenar dos mensajes de "validando": el registro obligatorio
  // ("¡Enhorabuena!") e ingresar/retirar fondos (MOVEMENTS.MANGOPAY_REVIEW).
  @Input() showPendingMessage = true;
  // Renovación del KYC: la sesión anterior está VALIDATED pero hay que hacer una verificación nueva
  @Input() kycRenewal = false;
  @Output() public finishEvent = new EventEmitter<any>();

  step = 0;
  loading = false;
  iframeLoaded = false;
  // PSC de una jurídica con la sesión IDV ya validada pero su propia verificación sin completar
  pendingPsc: MangopayIdvPscSession;
  // Ya se ha abierto la verificación de la PSC: al terminarla no se vuelve a buscar otra
  private pscOpened = false;
  // En web la foto se hace en el navegador del móvil (directamente o tras escanear el QR de Mangopay en
  // escritorio) y, si no tiene permiso de cámara, Mangopay no puede hacerla: avisamos antes de empezar
  showCameraTip = !Capacitor.isNativePlatform();
  cameraStepsOpen = false;
  // Pestaña de pasos abierta por defecto: la del sistema del usuario
  cameraOs: 'android' | 'ios' = /iPhone|iPad|iPod/i.test(navigator.userAgent)
    || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1) ? 'ios' : 'android';
  cameraSteps: string[] = [];
  // En web móvil la barra inferior del navegador tapa los botones de Mangopay: se deja espacio para hacer scroll
  mobileWebSpacing = isMobileWeb();
  // El enlace de la PSC no lleva redirectURL: Mangopay acaba en su propia pantalla ("Proceso de trabajo completado!")
  // y no llega el postMessage, así que se consulta el perfil hasta que la PSC deja de estar pendiente
  private pscStatusPolling: Subscription;

  urlSafeCategorize: SafeResourceUrl;
  urlSafeIdentityVerification: SafeResourceUrl;
  // El iframe de Mangopay lleva permiso de cámara salvo en web de escritorio (ahí Mangopay muestra el QR)
  cameraInIframe = mangopayIframeCamera();

  text: string;
  image: number;
  btnList = [
    {
      type: 'primaryDark',
      index: 1,
      title: this.translate.instant('register.messages.continue'),
    }
  ];

  // Cada paso (categorize / identityVerification) registra su propio listener y se
  // desregistra en cuanto lo consume, para que un postMessage duplicado o tardío del
  // paso anterior no pueda confundirse con la validación del paso siguiente.
  private categorizeMessageHandler = (e: MessageEvent) => this.handleCategorizeMessage(e);
  private identityVerificationMessageHandler = (e: MessageEvent) => this.handleIdentityVerificationMessage(e);

  constructor(
    private sanitizer: DomSanitizer,
    private cdRef: ChangeDetectorRef,
    private translate: TranslateService,
    private profileService: ProfileService,
    private elementRef: ElementRef,
  ) { }

  ngOnInit() {
    // La PSC pendiente se ofrece con la sesión VALIDATED o en REVIEW: Mangopay deja la sesión en revisión
    // (PSC_CONSISTENCY_CHECK) mientras la PSC no completa su propia verificación
    this.pendingPsc = getPendingPscSession(this.profile?.mangopayIdvSession);
    if (this.profile?.mangopayIdvSession?.status === 'VALIDATED' && !this.pendingPsc && !this.kycRenewal) {
      this.finish();
      return;
    }

    this.setInitialMessageParams();
    this.cdRef.detectChanges();
  }

  ngOnDestroy() {
    this.stopPscStatusPolling();
    window.removeEventListener('message', this.categorizeMessageHandler);
    window.removeEventListener('message', this.identityVerificationMessageHandler);
  }

  // Las páginas de vuelta de SCA, consentimientos e IDV mandan el mismo control_status: solo cuenta el de nuestro iframe
  private isFromOwnIframe(e: MessageEvent): boolean {
    const iframes: HTMLIFrameElement[] = Array.from(this.elementRef.nativeElement.querySelectorAll('iframe'));
    return iframes.some(iframe => iframe.contentWindow === e.source);
  }

  private handleCategorizeMessage(e: MessageEvent) {
    if ((e.data as any)?.func !== 'control_status' || !this.isFromOwnIframe(e)) { return; }
    window.removeEventListener('message', this.categorizeMessageHandler);
    const status = (e.data as any).data.value.controlStatus;
    const validated = status === 'VALIDATED';
    this.loading = true;
    this.profileService.saveCategorizeResult(validated).subscribe(() => {
      if (validated) {
        this.fetchIdentityVerification();
      } else {
        this.setErrorMessageParams();
        this.loading = false;
      }
      this.cdRef.detectChanges();
    });
    this.cdRef.detectChanges();
  }

  private handleIdentityVerificationMessage(e: MessageEvent) {
    if ((e.data as any)?.func !== 'control_status' || !this.isFromOwnIframe(e)) { return; }
    window.removeEventListener('message', this.identityVerificationMessageHandler);
    const status = (e.data as any).data.value.controlStatus;
    if (this.pscOpened) {
      this.stopPscStatusPolling();
      this.handleIdentityVerificationResult(status);
      return;
    }
    // Jurídica: Mangopay no incluye el selfie de la PSC en la verificación de la empresa, crea un enlace aparte
    // (webhook pscPending, segundos después). Sin abrirlo la empresa se queda en REVIEW y el wallet no se crea.
    this.loading = true;
    this.cdRef.detectChanges();
    this.waitForPendingPsc(5).subscribe(psc => {
      if (psc) {
        this.pendingPsc = psc;
        this.openPscVerification();
      } else {
        this.loading = false;
        this.handleIdentityVerificationResult(status);
      }
    });
  }

  private waitForPendingPsc(attempts: number): Observable<MangopayIdvPscSession> {
    if (this.profile?.profileType !== 2 || !this.profile?.username) {
      return of(null);
    }
    return timer(2000).pipe(
      switchMap(() => this.profileService.getProfile(this.profile.username)),
      catchError(() => of(null)),
      switchMap((profile: Profile) => {
        const psc = getPendingPscSession(profile?.mangopayIdvSession);
        if (psc || attempts <= 1) {
          return of(psc);
        }
        return this.waitForPendingPsc(attempts - 1);
      })
    );
  }

  private handleIdentityVerificationResult(status: string) {
    if (status === 'VALIDATED') {
      this.finish();
    } else if (!status) {
      // MangoPay no siempre añade controlStatus a la vuelta de este paso: sin dato no
      // podemos afirmar que haya fallado, así que no mostramos un error.
      if (this.showPendingMessage) {
        this.setPendingMessageParams();
      } else {
        this.finish();
      }
    } else {
      this.setErrorMessageParams();
    }
    this.cdRef.detectChanges();
  }

  start() {
    // Antes de cambiar nada en pantalla, para no perder el gesto del usuario (ver requestMobileWebCamera)
    const cameraRequest = requestMobileWebCamera();
    this.loading = true;
    this.iframeLoaded = false;
    this.cdRef.detectChanges();
    cameraRequest.then(() => this.startVerification());
  }

  private startVerification() {
    if (this.pendingPsc) {
      this.openPscVerification();
    } else if (this.profile && this.profile.isEnrolledSca) {
      this.fetchIdentityVerification();
    } else {
      window.addEventListener('message', this.categorizeMessageHandler);
      this.callCategorize();
    }
  }

  private callCategorize() {
    forkJoin([
      this.profileService.categorizeUser().pipe(
        catchError(() => timer(2000).pipe(switchMap(() => this.profileService.categorizeUser())))
      ),
      timer(500)
    ]).pipe(map(([resp]) => resp)).subscribe(resp => {
      const isCompany = this.profile && this.profile.profileType === 2;
      if (resp === 'Categorized' || (isCompany && !resp.returnUrl)) {
        this.fetchIdentityVerification();
      } else if (!resp.returnUrl) {
        timer(2000).subscribe(() => this.callCategorize());
      } else {
        this.urlSafeCategorize = this.sanitizer.bypassSecurityTrustResourceUrl(resp.returnUrl);
        this.loading = false;
        this.step = 1;
      }
      this.cdRef.detectChanges();
    }, () => {
      this.setErrorMessageParams();
      this.loading = false;
      this.cdRef.detectChanges();
    });
  }

  fetchIdentityVerification() {
    this.loading = true;
    this.iframeLoaded = false;
    this.cdRef.detectChanges();
    window.addEventListener('message', this.identityVerificationMessageHandler);
    this.callIdentityVerification();
  }

  private callIdentityVerification() {
    forkJoin([
      this.profileService.getIdentityVerification().pipe(
        catchError(() => timer(2000).pipe(switchMap(() => this.profileService.getIdentityVerification())))
      ),
      timer(500)
    ]).pipe(map(([resp]) => resp)).subscribe(resp => {
      if (!resp.hostedUrl) {
        timer(2000).subscribe(() => this.callIdentityVerification());
      } else {
        this.urlSafeIdentityVerification = this.sanitizer.bypassSecurityTrustResourceUrl(resp.hostedUrl);
        this.loading = false;
        this.step = 2;
      }
      this.cdRef.detectChanges();
    }, () => {
      this.setErrorMessageParams();
      this.loading = false;
      this.cdRef.detectChanges();
    });
  }

  // El enlace de la PSC ya viene en el perfil: se muestra en el mismo iframe que la verificación de identidad
  private openPscVerification() {
    this.pscOpened = true;
    this.iframeLoaded = false;
    window.addEventListener('message', this.identityVerificationMessageHandler);
    this.urlSafeIdentityVerification = this.sanitizer.bypassSecurityTrustResourceUrl(this.pendingPsc.hostedUrl);
    this.loading = false;
    this.step = 2;
    this.cdRef.detectChanges();
    this.startPscStatusPolling();
  }

  private startPscStatusPolling() {
    this.stopPscStatusPolling();
    if (!this.profile?.username) { return; }
    this.pscStatusPolling = interval(4000).pipe(
      switchMap(() => this.profileService.getProfile(this.profile.username).pipe(catchError(() => of(null))))
    ).subscribe((profile: Profile) => {
      const psc = profile?.mangopayIdvSession?.pscSessions?.find(p => p.pscId === this.pendingPsc?.pscId);
      if (!psc || psc.status === 'PENDING_VALIDATION') { return; }
      this.stopPscStatusPolling();
      window.removeEventListener('message', this.identityVerificationMessageHandler);
      if (psc.status === 'VALIDATED') {
        this.finish();
      } else {
        this.setErrorMessageParams();
      }
      this.cdRef.detectChanges();
    });
  }

  private stopPscStatusPolling() {
    this.pscStatusPolling?.unsubscribe();
    this.pscStatusPolling = null;
  }

  charged(event) {
    // Al insertar el iframe el navegador lanza un primer load de la página vacía (about:blank, aún sin src):
    // tomarlo como cargado quitaba el "Validando" antes de que llegara Mangopay y se veía la pantalla en blanco
    const src = (event?.target as HTMLIFrameElement)?.src;
    if (!src || src === 'about:blank') { return; }
    setTimeout(() => {
      this.iframeLoaded = true;
      this.cdRef.detectChanges();
    }, 1000);
    this.cdRef.detectChanges();
  }

  setInitialMessageParams() {
    this.image = 3;
    this.text = this.kycRenewal ? kycRenewalText(this.translate, 'KYC_RENEWAL.MODAL_TXT') : this.translate.instant('register.identity_verification.txt');
    this.btnList = [
      {
        type: 'primaryDark',
        index: 1,
        title: this.translate.instant('register.messages.continue'),
      }
    ];
    this.cdRef.detectChanges();
  }

  setErrorMessageParams() {
    this.step = 3;
    this.iframeLoaded = false;
    this.image = 1;
    this.text = this.translate.instant('SCA.ISSUE');
    this.btnList = [
      {
        type: 'primaryDark',
        index: 1,
        title: this.translate.instant('register.messages.continue'),
      }
    ];
    this.cdRef.detectChanges();
  }

  toggleCameraSteps() {
    this.cameraStepsOpen = !this.cameraStepsOpen;
    this.setCameraOs(this.cameraOs);
  }

  setCameraOs(os: 'android' | 'ios') {
    this.cameraOs = os;
    const key = os === 'ios' ? 'register.identity_verification.camera_steps_ios' : 'register.identity_verification.camera_steps_android';
    this.cameraSteps = this.splitCameraSteps(this.translate.instant(key));
    this.cdRef.detectChanges();
  }

  // Un paso por línea en SimpleLocalize (separados por "|"); si el texto es un único párrafo, se separa por frases
  private splitCameraSteps(text: string): string[] {
    // Sin lookbehind (?<=...): Safari no lo admite antes de iOS 16.4 y rompería el bundle entero
    const steps = text.includes('|') ? text.split('|') : text.replace(/\.\s+/g, '.\n').split('\n');
    return steps.map(step => step.trim()).filter(step => !!step);
  }

  setPendingMessageParams() {
    this.step = 4;
    this.iframeLoaded = false;
    this.image = 6;
    this.text = this.translate.instant('register.identity_verification.pending');
    this.btnList = [
      {
        type: 'primaryDark',
        index: 1,
        title: this.translate.instant('register.messages.continue'),
      }
    ];
    this.cdRef.detectChanges();
  }

  retry() {
    window.removeEventListener('message', this.categorizeMessageHandler);
    window.removeEventListener('message', this.identityVerificationMessageHandler);
    this.step = 0;
    this.pscOpened = false;
    this.stopPscStatusPolling();
    this.urlSafeCategorize = null;
    this.urlSafeIdentityVerification = null;
    this.setInitialMessageParams();
    this.cdRef.detectChanges();
  }

  onBtnEvent() {
    if (this.step === 3) {
      this.retry();
    } else {
      this.start();
    }
  }

  finish() {
    this.finishEvent.emit();
    this.cdRef.detectChanges();
  }

}
